
import { useState } from "react";
import {
  authAPI,
  storeCompany
} from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import logo from "../../../assets/logo.png";

export default function LoginScreen({ onBack }) {
  const { loginWithCompany } = useAuth();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    email: "",
    password: ""
  });

  // Handle input changes
  const setF = (key) => (e) => {
    setForm((f) => ({
      ...f,
      [key]: e.target.value
    }));

    setError("");
  };

  // Validate login form
  const validate = () => {
    if (!form.email.trim()) {
      return "Please enter your email address.";
    }

    if (!form.email.includes("@")) {
      return "Please enter a valid email address.";
    }

    if (!form.password) {
      return "Please enter your password.";
    }

    return null;
  };

  // Login
  const handleLogin = async (e) => {
    e.preventDefault();

    const validationError = validate();

    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Device details are fetched inside authAPI.login()
      const res = await authAPI.login({
        email: form.email.trim(),
        password: form.password
      });

      if (res?.status !== "success") {
        setError(
          res?.message || "Login failed. Please try again."
        );
        return;
      }

      // PHP response structure:
      // res.data.user
      // res.data.company

      const user = res.data?.user;
      const companyData = res.data?.company;

      if (!user || !companyData) {
        throw new Error(
          "User or company details are missing from the login response."
        );
      }

      // Normalize company data so it matches registration storage
      const company = {
        CM_Id: Number(companyData.CM_Id),
        CM_Name: companyData.CM_Name ?? "",
        CM_Email: companyData.CM_Email ?? "",
        CM_Address: companyData.CM_Address ?? "",
        CM_City: companyData.CM_City ?? "",
        CM_ContactNo: companyData.CM_ContactNo ?? "",
        CM_Logo: companyData.CM_Logo ?? "",
        CM_Reg_Date: companyData.CM_Reg_Date ?? null,
        CM_Expiry_Date: companyData.CM_Expiry_Date ?? null,
        CM_Status: Number(companyData.CM_Status ?? 0)
      };

      if (!company.CM_Id) {
        throw new Error("Invalid company ID received.");
      }

      // Store company details
      storeCompany(company);

      // Store logged-in user details separately
      sessionStorage.setItem(
        "oms_user",
        JSON.stringify(user)
      );

      // Update authentication context
      loginWithCompany(company);  
      navigate("/welcome", { replace: true });

    } catch (err) {
      console.error("[LoginScreen] Login error:", err);

      setError(
        err.message || "Unable to login. Please try again."
      );

    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">

      <div
        className="auth-card"
        style={{ maxWidth: 420 }}
      >

        {/* Logo */}
        <div className="auth-logo">

          <img
            src={logo}
            alt="TailorExpert"
            className="auth-logo-icon"
          />

          <h1 className="auth-logo-title">
            TailorExpert
          </h1>

          <p className="auth-logo-sub">
            Sign in to your tailoring shop
          </p>

        </div>

        {/* Error Message */}
        {error && (
          <div
            className="error-msg"
            style={{ marginBottom: 16 }}
          >
            {error}
          </div>
        )}

        {/* Login Form */}
        <form
          className="auth-form"
          onSubmit={handleLogin}
        >

          {/* Email */}
          <div className="form-group">

            <label className="form-label">
              Email Address{" "}
              <span className="form-required">*</span>
            </label>

            <input
              className="form-input"
              type="email"
              placeholder="Enter your registered email"
              value={form.email}
              onChange={setF("email")}
              autoComplete="username"
              autoFocus
              disabled={loading}
            />

          </div>

          {/* Password */}
          <div className="form-group">

            <label className="form-label">
              Password{" "}
              <span className="form-required">*</span>
            </label>

            <input
              className="form-input"
              type="password"
              placeholder="Enter your password"
              value={form.password}
              onChange={setF("password")}
              autoComplete="current-password"
              disabled={loading}
            />

          </div>

          {/* Buttons */}
          <div
            style={{
              display: "flex",
              gap: 10,
              marginTop: 8
            }}
          >

            {onBack && (
              <button
                type="button"
                className="btn btn-ghost"
                onClick={onBack}
                disabled={loading}
                style={{ flex: 1 }}
              >
                ← Back
              </button>
            )}

            <button
              type="submit"
              className="btn btn-primary btn-lg"
              disabled={loading}
              style={{ flex: 2 }}
            >

              {loading ? (
                <>
                  <span style={spinnerStyle} />
                  Logging in...
                </>
              ) : (
                "Login →"
              )}

            </button>

          </div>

        </form>

      </div>

    </div>
  );
}

/*
|--------------------------------------------------------------------------
| Spinner
|--------------------------------------------------------------------------
*/

const spinnerStyle = {
  display: "inline-block",
  width: 14,
  height: 14,
  border: "2px solid rgba(255,255,255,0.3)",
  borderTop: "2px solid #fff",
  borderRadius: "50%",
  animation: "spin 0.7s linear infinite",
  marginRight: 6
};
